import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { Menu, X, ShieldCheck, ChevronDown } from "lucide-react";
import { TOPICS } from "@/lib/topics";

const LOGO_MARK = "https://customer-assets.emergentagent.com/job_before-the-loss-1/artifacts/hyk3lwcd_514CC5D3-9081-44D7-8DD9-B78FADD7368F.png";

const NAV = [
  { label: "Home", to: "/" },
  { label: "Risk Score", to: "/risk-score" },
  { label: "About", to: "/about" },
  { label: "Services", to: "/services", sub: true },
  { label: "Insights", to: "/insights" },
  { label: "Partners", to: "/partners" },
  { label: "Contact", to: "/contact" },
];

const slugOf = (label) => label.toLowerCase().replace(/\s+/g, "-");

const ServicesDropdown = () => (
  <div className="absolute left-1/2 -translate-x-1/2 top-full pt-4 opacity-0 invisible group-hover:opacity-100 group-hover:visible group-focus-within:opacity-100 group-focus-within:visible transition-opacity duration-200" data-testid="services-dropdown">
    <div className="w-72 rounded-lg border border-white/10 bg-[#0a0a0b]/95 backdrop-blur-xl p-2 shadow-2xl">
      {TOPICS.map((t) => (
        <Link key={t.slug} to={`/topics/${t.slug}`} className="block rounded-md px-3 py-2.5 font-display text-[13px] text-zinc-300 hover:text-white hover:bg-[#a2020b]/15 transition-colors" data-testid={`dropdown-${t.slug}`}>
          {t.title}
        </Link>
      ))}
    </div>
  </div>
);

const MobileMenu = ({ close, pathname }) => {
  const [svcOpen, setSvcOpen] = useState(false);
  const linkCls = "font-display text-base text-zinc-200 hover:text-[#a2020b] transition-colors py-3";
  return (
    <div className="lg:hidden border-t border-white/5 bg-[#050505]/95 backdrop-blur-xl max-h-[calc(100vh-5rem)] overflow-y-auto" data-testid="mobile-menu" style={{ animation: "btr-page-enter 0.35s ease-out both" }}>
      <div className="px-5 py-7 flex flex-col">
        {NAV.map((item) =>
          item.sub ? (
            <div key={item.to} className="border-b border-white/5">
              <div className="flex items-center justify-between">
                <Link to={item.to} onClick={close} className={`${linkCls} flex-1 ${pathname === item.to ? "text-[#a2020b]" : ""}`} data-testid="mobile-nav-services">{item.label}</Link>
                <button onClick={() => setSvcOpen(!svcOpen)} className="p-3 -mr-3 text-zinc-300" aria-label="Toggle services" data-testid="mobile-services-toggle">
                  <ChevronDown className={`w-5 h-5 transition-transform ${svcOpen ? "rotate-180" : ""}`} />
                </button>
              </div>
              {svcOpen && (
                <div className="pb-3 pl-3 flex flex-col" data-testid="mobile-services-submenu">
                  {TOPICS.map((t) => (
                    <Link key={t.slug} to={`/topics/${t.slug}`} onClick={close} className="font-body text-sm text-zinc-400 hover:text-white py-2.5" data-testid={`mobile-sub-${t.slug}`}>{t.title}</Link>
                  ))}
                </div>
              )}
            </div>
          ) : (
            <Link key={item.to} to={item.to} onClick={close} className={`${linkCls} border-b border-white/5 ${pathname === item.to ? "text-[#a2020b]" : ""}`} data-testid={`mobile-nav-${slugOf(item.label)}`}>{item.label}</Link>
          )
        )}
        <Link to="/contact" onClick={close} className="btr-btn-primary w-full mt-5" data-testid="mobile-schedule-btn">
          <ShieldCheck className="w-4 h-4" /> Schedule Consultation
        </Link>
      </div>
    </div>
  );
};

export const Header = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const isActive = (item) => (item.sub ? pathname === "/services" || pathname.startsWith("/topics") : pathname === item.to);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 18);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header className={`btr-header fixed top-0 left-0 right-0 z-50 ${scrolled || pathname !== "/" ? "scrolled" : ""}`} data-testid="site-header">
      <div className="max-w-7xl mx-auto px-5 sm:px-6 h-20 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-3 group" data-testid="header-logo-link">
          <img src={LOGO_MARK} alt="Before The Risk" className="h-10 sm:h-12 lg:h-14 w-auto object-contain transition-transform group-hover:scale-[1.04]" />
        </Link>

        <nav className="hidden lg:flex items-center gap-5 xl:gap-8">
          {NAV.map((item) => (
            <div key={item.to} className={item.sub ? "relative group" : ""}>
              <Link
                to={item.to}
                className={`btr-nav-link font-display text-[13px] tracking-wide transition-colors inline-flex items-center gap-1 ${isActive(item) ? "text-white is-active" : "text-zinc-300 hover:text-white"}`}
                data-testid={`nav-${slugOf(item.label)}`}
              >
                {item.label}
                {item.sub && <ChevronDown className="w-3 h-3 transition-transform group-hover:rotate-180" />}
              </Link>
              {item.sub && <ServicesDropdown />}
            </div>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <Link to="/contact" className="btr-btn-primary !hidden md:!inline-flex lg:!hidden xl:!inline-flex" data-testid="header-schedule-btn">
            <ShieldCheck className="w-4 h-4" />
            Schedule Consultation
          </Link>
          <button className="lg:hidden text-white p-2 border border-white/10 rounded-sm" onClick={() => setOpen(!open)} aria-label="Toggle menu" data-testid="mobile-menu-toggle">
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>
      {open && <MobileMenu close={() => setOpen(false)} pathname={pathname} />}
    </header>
  );
};
